import { Box, Grid, TextField, Typography } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { Map } from "schemas/functions";

import StyledCard from "components/Styled/Card";
import QuestionChip from "components/Styled/QuestionChip";
import { makeDeepCopy } from "utils/updateLocalState";

import StyledOnboardingPage from "../StyledOnboardingPage";
import styles from "../styles";

enum IncomeMethod {
  getting_started = "I'm just getting started (not monetizing yet)",
  brand_deals = "Brand deals & sponsorships",
  affiliate_marketing = "Affiliate marketing",
  selling_my_own_products = "Selling my own products (physical or digital)",
  paid_subscriptions = "Paid subscriptions or memberships",
  platform_payouts = "Platform payouts / ad revenue",
  live_streaming = "Live streaming tips or donations",
  other = "Other",
}

const MONTHLY_INCOME_OPTIONS = [
  {
    min: 0,
    max: 0,
    label: "$0",
  },
  {
    min: 1,
    max: 1000,
    label: "$1 - $1,000",
  },
  {
    min: 1001,
    max: 10000,
    label: "$1,001 - $10,000",
  },
  {
    min: 10001,
    max: 25000,
    label: "$10,001 - $25,000",
  },
  {
    min: 25001,
    max: 25001,
    label: "$25,000+",
  },
];

interface IncomeFormProps {
  handleNext: (onboardingForm: Map) => void;
  handleSkip?: () => void;
  isProfilePage?: boolean;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  handleBack?: () => void;
}

const IncomeForm = ({
  handleNext,
  handleSkip,
  onboardingForm,
  setOnboardingForm,
  handleBack,
}: IncomeFormProps) => {
  const handleUpdateList = (optionLabel: string) => {
    setOnboardingForm((prev) => {
      const copy = makeDeepCopy(prev);
      const selectedValue = MONTHLY_INCOME_OPTIONS.find(
        (option) => option.label === optionLabel,
      );
      copy.monthlyIncome = [selectedValue?.min, selectedValue?.max];
      return copy;
    });
  };

  const handleAddIncomeMethod = (selectedMethod: IncomeMethod) => {
    setOnboardingForm((prev) => {
      const currentMethods = prev.incomeMethod || [];
      let updatedMethods = [...currentMethods];
      if (updatedMethods.includes(selectedMethod)) {
        updatedMethods = updatedMethods.filter(
          (method: string) => method !== selectedMethod,
        );
      } else {
        updatedMethods.push(selectedMethod);
      }
      return { ...prev, incomeMethod: updatedMethods };
    });
  };

  return (
    <StyledOnboardingPage
      title="Your Earning as a Creator 💵"
      stepNumber={3}
      handleBack={handleBack}
      handleSkip={handleSkip}
      handleNext={() => handleNext(onboardingForm)}
    >
      <StyledCard styles={{ px: 3, py: 4 }}>
        <Box sx={styles.questionBox}>
          <Typography gutterBottom>
            How do you currently earn income from your creator business?
          </Typography>
          <Typography variant="caption" color="textSecondary" paragraph>
            Select all that apply.
          </Typography>

          <Grid container gap={1} alignItems="center">
            {Object.values(IncomeMethod).map((method) => (
              <QuestionChip
                onClick={() => {
                  handleAddIncomeMethod(method);
                }}
                isSelected={onboardingForm?.incomeMethod?.includes(method)}
                label={method}
                key={method}
              />
            ))}
            {onboardingForm?.incomeMethod?.includes(IncomeMethod.other) && (
              <TextField
                placeholder="Please specify"
                variant="standard"
                value={onboardingForm?.incomeMethodOther || ""}
                onChange={(e) =>
                  setOnboardingForm((prev) => ({
                    ...prev,
                    incomeMethodOther: e.target.value,
                  }))
                }
              />
            )}
          </Grid>
        </Box>

        <Box sx={styles.questionBox}>
          <Typography gutterBottom>
            How much revenue did you earn from your creator business last month?
          </Typography>
          <Typography variant="caption" color="textSecondary" paragraph>
            Select an option below.
          </Typography>

          <Grid container gap={1}>
            {MONTHLY_INCOME_OPTIONS.map((option) => (
              <QuestionChip
                onClick={() => {
                  handleUpdateList(option.label);
                }}
                isSelected={
                  onboardingForm?.monthlyIncome?.[0] === option.min &&
                  onboardingForm?.monthlyIncome?.[1] === option.max
                }
                label={option.label}
                key={option.label}
              />
            ))}
          </Grid>
        </Box>
      </StyledCard>
    </StyledOnboardingPage>
  );
};

export default IncomeForm;
